/*
 * AR Keep Shopping
 * A lightweight "keep browsing" grid for the product page. Loads product data
 * from the collection's products.json (plain JSON, no heavy card components) and
 * renders simple image + title + price cards. First page loads when the section
 * nears the viewport; each "Load more" tap appends the next page; the button
 * hides at the last page. A "View all" link drops the shopper into the full
 * collection.
 *
 * Why JSON instead of scraping the collection HTML: the theme's real product
 * cards are heavy custom elements (quick-add, swatches, media) — injecting a
 * page of 24 at once locks the main thread. Light cards stay smooth.
 */
(function () {
  'use strict';

  var PER_PAGE = 12;

  // Color name -> swatch color. MIRRORS snippets/ar-swatch-hex.liquid — keep the
  // two in sync when new catalog colors appear (that snippet is the canonical map).
  var COLOR_HEX = {
    'black': '#141414', 'elegant black': '#141414',
    'white': '#ffffff',
    'off white': '#f3efe4', 'cream white': '#f3efe4',
    'cream': '#f2e8d0',
    'oatmeal': '#e2d8c4',
    'beige': '#d8c5a3', 'sand beige': '#d8c5a3',
    'champagne': '#eddfbf',
    'taupe': '#b6a793',
    'khaki': '#8f7d50', 'light khaki': '#c4b483',
    'dust storm': '#cabdad',
    'skin powder': '#e7cdbe',
    'hazelnut': '#a67b53', 'light brown': '#a67b53',
    'tan': '#d2b48c',
    'brown': '#6b4f3a',
    'coffee brown': '#3c2c20', 'dark brown': '#3c2c20',
    'gold': '#c6a02a',
    'yellow': '#e7cf5f',
    'light yellow': '#f4edad', 'pastel yellow': '#f4edad',
    'apricot': '#f1b183', 'light apricot': '#f7d6bb',
    'orange': '#e08234',
    'tangerine': '#f08023',
    'coral': '#ff7f57',
    'rust': '#b1490f',
    'brick red': '#8c3a2d',
    'red': '#b2352f',
    'rouge color': '#c02a2a',
    'rose red': '#c72c48', 'cerise': '#c72c48',
    'wine red': '#6f2a34',
    'burgundy': '#6a2733',
    'pink': '#f3b4c1',
    'blush pink': '#e7b6ba',
    'dusty pink': '#d6a1a6',
    'fuchsia pink': '#c0448f',
    'purple': '#6a4c93',
    'light purple': '#c6b5d8',
    'grape purple': '#6c2ca4',
    'violet': '#7f5fb0',
    'blue': '#47699f',
    'light blue': '#bcd4e6',
    'sky blue': '#a7c7e7',
    'dusty blue': '#8ea9c1',
    'french blue': '#6d8fb3',
    'lake blue': '#3f7fa6',
    'peacock blue': '#1a5f7a',
    'royal': '#2a4b9b', 'royal blue': '#2a4b9b',
    'navy': '#1e2a46', 'navy blue': '#1e2a46', 'dark navy': '#1e2a46', 'dark navy blue': '#1e2a46', 'dark blue': '#1e2a46',
    'medium': '#6a89b8',
    'green': '#4f7d4f',
    'light green': '#b7d3a8',
    'dark green': '#183f2a',
    'army green': '#4b5320',
    'avocado green': '#5a7302',
    'bean green': '#4a7023',
    'sage': '#9caf88',
    'matcha': '#a9ba9d', 'matcha green': '#a9ba9d',
    'peacock green': '#1b7a6e',
    'teal green': '#2a8c82',
    'deep teal': '#0f4d4e',
    'gray': '#8b8b8b', 'grey': '#8b8b8b',
    'dark gray': '#4a4a4a',
    'multi': 'conic-gradient(#e0a4b4, #efd9a6, #a9c7de, #b7d3a8, #c6b5d8, #e0a4b4)',
    'floral': 'conic-gradient(#e6c1cf, #d9b48c, #9db884, #cbb7d6, #e6c1cf)'
  };

  function normColor(name) {
    var n = String(name == null ? '' : name).toLowerCase().replace(/-/g, ' ').trim();
    n = n.replace(/\s{2,}/g, ' ');
    n = n.replace(/ skirt/g, '').replace(/ top/g, '').replace(/ set/g, '').trim();
    return n;
  }

  function swatchHex(name) {
    return COLOR_HEX[normColor(name)] || '';
  }

  function money(value) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    // products.json returns major units (e.g. "89.95") in the store currency.
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function sizedImage(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function swatchesHTML(product) {
    var opt = (product.options || []).filter(function (o) {
      return /colou?r/i.test(o.name);
    })[0];
    if (!opt || !opt.values || !opt.values.length) return '';

    var vals = opt.values;
    var MAX_DOTS = 5;
    var dots = '';
    for (var i = 0; i < vals.length && i < MAX_DOTS; i++) {
      var hex = swatchHex(vals[i]);
      var bg = hex
        ? (hex.indexOf('gradient') > -1 ? 'background:' + hex : 'background-color:' + hex)
        : 'background-color:#d9d4cc';
      dots += '<span class="ks-card__swatch" style="' + bg + '" title="' + esc(vals[i]) + '"></span>';
    }
    var extra = vals.length > MAX_DOTS ? vals.length - MAX_DOTS : 0;
    return (
      '<span class="ks-card__swatches">' +
        dots +
        (extra ? '<span class="ks-card__swatch-more">+' + extra + '</span>' : '') +
      '</span>'
    );
  }

  function cardHTML(product) {
    var handle = product.handle;
    var titleParts = String(product.title || '').split(' — ');
    var name = esc(titleParts[0]);
    var desc = esc(titleParts.slice(1).join(' — '));

    var v = (product.variants && product.variants[0]) || {};
    var price = v.price;
    var compare = v.compare_at_price;
    var onSale = compare && parseFloat(compare) > parseFloat(price);

    var img = product.images && product.images[0] ? product.images[0].src : '';
    var priceHTML = onSale
      ? '<span class="ks-card__price"><s class="ks-card__was">' + money(compare) + '</s> <ins class="ks-card__now">' + money(price) + '</ins></span>'
      : '<span class="ks-card__price">' + money(price) + '</span>';

    return (
      '<li class="ks-card">' +
        '<a class="ks-card__link" href="/products/' + esc(handle) + '">' +
          '<div class="ks-card__media">' +
            (img ? '<img class="ks-card__img" src="' + esc(sizedImage(img, 500)) + '" alt="' + name + '" loading="lazy" decoding="async">' : '') +
          '</div>' +
          '<div class="ks-card__info">' +
            '<span class="ks-card__name">' + name + '</span>' +
            (desc ? '<span class="ks-card__desc">' + desc + '</span>' : '') +
            priceHTML +
            swatchesHTML(product) +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  function init(root) {
    if (root.dataset.ksInit === '1') return;
    root.dataset.ksInit = '1';

    var grid = root.querySelector('[data-ks-grid]');
    var button = root.querySelector('[data-ks-more]');
    var base = (root.getAttribute('data-ks-base') || '/collections/all').replace(/\/$/, '');
    var exclude = (root.getAttribute('data-ks-exclude') || '').trim();

    if (!grid) return;

    var nextPage = 1;
    var done = false;
    var loading = false;
    var started = false;

    function clearSkeletons() {
      var skels = grid.querySelectorAll('[data-ks-skeleton]');
      for (var i = 0; i < skels.length; i++) skels[i].remove();
    }

    function setBusy(state) {
      if (!button) return;
      if (state) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    function updateButton() {
      if (!button) return;
      if (done) button.classList.add('is-hidden');
      else button.classList.remove('is-hidden');
    }

    function loadPage(reveal) {
      if (loading || done) return;
      loading = true;
      setBusy(true);
      var prevCount = grid.querySelectorAll('.ks-card').length;

      fetch(base + '/products.json?limit=' + PER_PAGE + '&page=' + nextPage)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];

          if (nextPage === 1) clearSkeletons();

          // Fewer than a full page back means we've reached the end.
          if (products.length < PER_PAGE) done = true;

          var html = '';
          var shown = 0;
          for (var i = 0; i < products.length; i++) {
            if (products[i].handle === exclude) continue;
            html += cardHTML(products[i]);
            shown++;
          }

          if (nextPage === 1 && shown === 0 && done) {
            root.remove();
            return;
          }

          if (html) grid.insertAdjacentHTML('beforeend', html);

          nextPage++;
          loading = false;
          setBusy(false);
          updateButton();

          // On the mobile horizontal carousel, new cards append off-screen to
          // the right. If this load was user-triggered, scroll them into view so
          // the button visibly does something.
          if (reveal) {
            var cards = grid.querySelectorAll('.ks-card');
            var firstNew = cards[prevCount];
            if (firstNew && grid.scrollWidth > grid.clientWidth + 4) {
              firstNew.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
            }
          }
        })
        .catch(function () {
          loading = false;
          setBusy(false);
          if (nextPage === 1) clearSkeletons();
          updateButton();
        });
    }

    if (button) button.addEventListener('click', function () { loadPage(true); });

    // Auto-load as the mobile carousel is swiped near its right end, so it
    // browses like the Recently Viewed carousel. (No-op on the desktop grid,
    // which doesn't scroll horizontally.)
    grid.addEventListener('scroll', function () {
      if (loading || done) return;
      if (grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 250) loadPage(false);
    });

    function start() {
      if (started) return;
      started = true;
      loadPage(false);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              io.disconnect();
              start();
              break;
            }
          }
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(root);
    } else {
      start();
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ks-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
